import FlexContainer from './FlexContainer';
import Filters from './Filters';
import SelectGuestType from './SelectGuestType';
import GuestList from './GuestList';
import DatabasePagination from './DatabasePagination';
import Button from './Button';
import useFilteredGuests from '../hooks/useFilteredGuests';
import { MouseEvent, useContext, useEffect, useState } from 'react';
import { GuestFilters } from '../interfaces/Filters';
import getGuest from '../functions/getGuest';
import GuestI from '../interfaces/Guest';
import { Redirect, useHistory } from 'react-router-dom';
import Action from '../interfaces/Action';
import useGuests from '../hooks/useGuests';
import GlobalContextI from '../interfaces/GlobalContext';
import GlobalContext from '../contexts/GlobalContext';
import GuestDatabaseModal from './modals/GuestDatabaseModal';
import Loading from './Loading';

const EMPTY_FILTERS: GuestFilters = {
    query: '',
    ageMin: '',
    ageMax: '',
    dateFrom: '',
    dateTo: '',
    standing: '',
    tos: '',
};

const GuestDatabase = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string>('limited');
    const [filters, setFilters] = useState<GuestFilters>(EMPTY_FILTERS);
    const [action, setAction] = useState<Action>({
        status: false,
        type: 'unknown',
    });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const hasActiveFilter = Boolean(
        filters.query ||
            filters.ageMin ||
            filters.ageMax ||
            filters.dateFrom ||
            filters.dateTo ||
            filters.standing ||
            filters.tos
    );
    const fetchEnabled = filter === 'allAccess' || hasActiveFilter;
    const { guests } = useGuests(filter, action, setLoading, fetchEnabled);
    const { guest, setGuest } = useContext<GlobalContextI>(GlobalContext);
    const { filteredGuests } = useFilteredGuests(guests, filters);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const history = useHistory();

    useEffect(() => {
        setPageNumber(1);
    }, [filters, filter]);

    useEffect(() => {
        setFilters(EMPTY_FILTERS);
    }, [filter]);

    const handleEdit = (e: MouseEvent<HTMLTableDataCellElement>) => {
        const guest = getGuest(e.currentTarget.id, guests) as GuestI;
        setGuest({ ...guest, page: history.location.pathname });
        setAction({ status: true, type: 'edit' });
    };

    const openModal = (e: MouseEvent<HTMLButtonElement>) => {
        const guest = getGuest(
            e.currentTarget.children[0].className,
            guests
        ) as GuestI;

        setGuest(guest);
        setIsOpen(true);
    };

    if (loading) {
        return <Loading loading={loading} />;
    }

    if (action.status) {
        return <Redirect to={`/editGuest/${guest?.type}`} />;
    }

    const visibleCount = filteredGuests.filter(
        (g) => g.type === (filter === 'allAccess' ? 'allAccess' : 'limited')
    ).length;

    const filterTypeForSort = filters.dateFrom || filters.dateTo ? 'date' : 'all';

    return (
        <FlexContainer>
            <Filters filters={filters} setFilters={setFilters} />
            <SelectGuestType filter={filter} setFilter={setFilter} />
            <div className="user-container__text">
                <h2>{filter} Guests:</h2>
                <p>{visibleCount} match{visibleCount === 1 ? '' : 'es'}</p>
            </div>
            <Button
                color={'red'}
                margin={true}
                onClick={() => setFilters(EMPTY_FILTERS)}
            >
                Clear Filters
            </Button>
            {!fetchEnabled ? (
                <p
                    style={{
                        textAlign: 'center',
                        color: '#aaa',
                        margin: '2rem 0',
                    }}
                >
                    Enter a search or filter above to view limited guests.
                </p>
            ) : (
                <>
                    {guests !== null && (
                        <GuestList
                            guestType={filter === 'allAccess' ? filter : 'limited'}
                            filterType={filterTypeForSort}
                            pageNumber={pageNumber}
                            filteredGuests={filteredGuests}
                            handleEdit={handleEdit}
                            openModal={openModal}
                        />
                    )}
                    <DatabasePagination
                        pageNumber={pageNumber}
                        setPageNumber={setPageNumber}
                        filteredGuests={filteredGuests}
                    />
                </>
            )}
            <Button
                color={'red'}
                margin={true}
                onClick={() => history.push('/dashboard')}
            >
                Back
            </Button>
            <GuestDatabaseModal
                guest={guest}
                guests={guests}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setLoading={setLoading}
                setAction={setAction}
            />
        </FlexContainer>
    );
};

export default GuestDatabase;
